import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Activity, Lock, ShieldCheck, KeyRound } from "lucide-react";
import { SliderCaptcha } from "../components/SliderCaptcha";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [preToken, setPreToken] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!captcha) { setErr("Lütfen yapboz parçasını boşluğa sürükleyin."); return; }
    setLoading(true);
    const r = await login(email, password, captcha);
    setLoading(false);
    if (r.ok) nav("/");
    else if (r.mfa) { setPreToken(r.pre_token); setCode(""); }
    else { setErr(r.error); setCaptcha(null); setCaptchaKey((k) => k + 1); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await verifyMfa(preToken, code);
    setLoading(false);
    if (r.ok) nav("/");
    else setErr(r.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0B0E17] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#3B82F6 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="w-full max-w-md relative fade-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-lg bg-[#3B82F6]/15 border border-[#3B82F6]/30 flex items-center justify-center">
            <Activity className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="font-head text-xl font-extrabold tracking-tight">Rotarsi Al Sat Botu</h1>
            <p className="text-xs text-[#64748B]">Binance Futures Demo İşlem Terminali</p>
          </div>
        </div>

        {preToken ? (
        <form onSubmit={submitCode} data-testid="mfa-form" className="bg-[#121724] border border-[#1E2738] rounded-lg p-6 sm:p-7 space-y-5">
          <div className="flex items-center gap-2 text-[#94A3B8] text-sm font-semibold"><KeyRound className="h-4 w-4 text-[#F59E0B]" /> İkinci Adım — Google Authenticator</div>
          <p className="text-xs text-[#64748B]">Authenticator uygulamanızdaki 6 haneli kodu girin. Telefonunuza erişemiyorsanız kurtarma kodlarınızdan birini kullanın.</p>
          <input data-testid="mfa-code-input" autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456 veya ABCD-EF12"
            className="w-full bg-[#0B0E17] border border-[#2A364F] rounded-md px-3 py-3 text-center text-lg tracking-[0.3em] font-mono outline-none focus:border-[#3B82F6]" />
          {err && <div data-testid="login-error" className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md px-3 py-2">{err}</div>}
          <button data-testid="mfa-submit-button" type="submit" disabled={loading || code.trim().length < 6} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white font-semibold text-sm rounded-md py-2.5">{loading ? "Doğrulanıyor…" : "Doğrula ve Giriş Yap"}</button>
          <button type="button" data-testid="mfa-back-button" onClick={() => { setPreToken(null); setErr(""); setCaptcha(null); setCaptchaKey((k) => k + 1); }} className="w-full text-xs text-[#64748B] hover:text-[#F1F5F9]">← Şifreyle girişe dön</button>
        </form>
        ) : (
        <form onSubmit={submit} className="bg-[#121724] border border-[#1E2738] rounded-lg p-6 sm:p-7 space-y-5">
          <div className="flex items-center gap-2 text-[#94A3B8] text-sm font-semibold">
            <Lock className="h-4 w-4" /> Yönetici Girişi
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">E-posta</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0B0E17] border border-[#2A364F] rounded-md px-3 py-2.5 text-sm font-mono outline-none focus:border-[#3B82F6] transition-colors"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Şifre</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#0B0E17] border border-[#2A364F] rounded-md px-3 py-2.5 text-sm font-mono outline-none focus:border-[#3B82F6] transition-colors"
            />
          </div>

          <SliderCaptcha onChange={setCaptcha} resetKey={captchaKey} />

          {err && (
            <div data-testid="login-error" className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading || !captcha}
            className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
          >
            {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
          </button>

          <div className="flex items-start gap-2 text-[11px] text-[#64748B] pt-1">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#10B981]" />
            <span>Tek kullanıcı erişimi · yapboz doğrulaması · isteğe bağlı Google Authenticator ikinci adımı (Ayarlar). Demo borsası — gerçek para riskte değildir.</span>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
